import axiosInstance from '../Api/axiosInstance';

// Create a new project for a specific company
export const createProject = (data, companyId) =>
	axiosInstance.post(`/projects/${companyId}/project`, data);

// Get all projects for a specific company
export const getCompanyProjects = (companyId) =>
	axiosInstance.get(`/projects/${companyId}/projects`);

// Get all projects linked to a specific employee
export const getUserProjects = (employeeId) =>
	axiosInstance.get(`/projects/user/${employeeId}/projects`);

// Get a specific project by ID for a given company
export const getProjectById = (projectId, companyId) =>
	axiosInstance.get(`/projects/${companyId}/project/${projectId}`);

// Update project details (partial updates allowed)
export const updateProject = (projectId, data, companyId) =>
	axiosInstance.put(`/projects/${companyId}/project/${projectId}`, data);

// Delete a project
export const deleteProject = (projectId, companyId) =>
	axiosInstance.delete(`/projects/${companyId}/project/${projectId}`);

// Update task status for a given task
export const updateTaskStatus = (projectId, taskId, status, reviewer) =>
	axiosInstance.put(`/projects/${projectId}/task/${taskId}`, { status, reviewer });

// Update subtask status for a given subtask within a task
export const updateSubtaskStatus = (projectId, taskId, subtaskId, status, reviewer) =>
	axiosInstance.put(
		`/projects/${projectId}/task/${taskId}/subtask/${subtaskId}`,
		{ status, reviewer },
	);

export const updateTaskAssignees = (projectId, taskId, assignedTo, subtaskId = null) => {
	if (subtaskId) {
		return axiosInstance.put(
			`/tasks/${projectId}/task/${taskId}/subtask/${subtaskId}/assignees`,
			{ assignedTo },
		);
	}

	return axiosInstance.put(`/tasks/${projectId}/task/${taskId}/assignees`, {
		assignedTo,
	});
};

export const updateTaskPriority = (projectId, taskId, priority, subtaskId = null) => {
	if (subtaskId) {
		return axiosInstance.put(
			`/tasks/${projectId}/task/${taskId}/subtask/${subtaskId}/priority`,
			{ priority },
		);
	}

	return axiosInstance.put(`/tasks/${projectId}/task/${taskId}/priority`, {
		priority,
	});
};

// Add a document to a project
export const addDocument = (companyId, projectId, documentData) =>
	axiosInstance.post(
		`/projects/${companyId}/project/${projectId}/document`,
		documentData,
	);

// Add a risk to a project
export const addRisk = (companyId, projectId, riskData) =>
	axiosInstance.post(
		`/projects/${companyId}/project/${projectId}/risk`,
		riskData,
	);

// Update risk status
export const updateRiskStatus = (
	companyId,
	projectId,
	riskId,
	status,
	note,
	probability,
	impact,
	category,
) =>
	axiosInstance.put(
		`/projects/${companyId}/project/${projectId}/risk/${riskId}`,
		{ status, note, probability, impact, category },
	);

// Add a task to a project
export const addTask = (projectId, taskData) =>
	axiosInstance.post(`/projects/${projectId}/task`, taskData);

// Add a subtask to a task
export const addSubtask = (projectId, taskId, subtaskData) =>
	axiosInstance.post(
		`/projects/${projectId}/task/${taskId}/subtask`,
		subtaskData,
	);

// Task file attachments
export const addTaskFile = (projectId, taskId, fileData) =>
	axiosInstance.post(`/projects/${projectId}/task/${taskId}/files`, fileData);

export const deleteTaskFile = (projectId, taskId, fileId) =>
	axiosInstance.delete(`/projects/${projectId}/task/${taskId}/files/${fileId}`);

// Subtask file attachments
export const addSubtaskFile = (projectId, taskId, subtaskId, fileData) =>
	axiosInstance.post(
		`/projects/${projectId}/task/${taskId}/subtask/${subtaskId}/files`,
		fileData,
	);

export const deleteSubtaskFile = (projectId, taskId, subtaskId, fileId) =>
	axiosInstance.delete(
		`/projects/${projectId}/task/${taskId}/subtask/${subtaskId}/files/${fileId}`,
	);

// Task & subtask comments (rich text + mentions + linked tasks/subtasks)
export const addTaskComment = (projectId, taskId, payload) =>
	axiosInstance.post(`/projects/${projectId}/task/${taskId}/comments`, payload);

export const deleteTaskComment = (projectId, taskId, commentId) =>
	axiosInstance.delete(
		`/projects/${projectId}/task/${taskId}/comments/${commentId}`,
	);

export const editTaskComment = (projectId, taskId, commentId, payload) =>
	axiosInstance.patch(
		`/projects/${projectId}/task/${taskId}/comments/${commentId}`,
		payload,
	);

export const addSubtaskComment = (projectId, taskId, subtaskId, payload) =>
	axiosInstance.post(
		`/projects/${projectId}/task/${taskId}/subtask/${subtaskId}/comments`,
		payload,
	);

export const deleteSubtaskComment = (projectId, taskId, subtaskId, commentId) =>
	axiosInstance.delete(
		`/projects/${projectId}/task/${taskId}/subtask/${subtaskId}/comments/${commentId}`,
	);

export const editSubtaskComment = (
	projectId,
	taskId,
	subtaskId,
	commentId,
	payload,
) =>
	axiosInstance.patch(
		`/projects/${projectId}/task/${taskId}/subtask/${subtaskId}/comments/${commentId}`,
		payload,
	);

// Comment replies
export const addTaskCommentReply = (projectId, taskId, commentId, payload) =>
	axiosInstance.post(
		`/projects/${projectId}/task/${taskId}/comments/${commentId}/replies`,
		payload,
	);

export const deleteTaskCommentReply = (projectId, taskId, commentId, replyId) =>
	axiosInstance.delete(
		`/projects/${projectId}/task/${taskId}/comments/${commentId}/replies/${replyId}`,
	);

export const addSubtaskCommentReply = (
	projectId,
	taskId,
	subtaskId,
	commentId,
	payload,
) =>
	axiosInstance.post(
		`/projects/${projectId}/task/${taskId}/subtask/${subtaskId}/comments/${commentId}/replies`,
		payload,
	);

export const deleteSubtaskCommentReply = (
	projectId,
	taskId,
	subtaskId,
	commentId,
	replyId,
) =>
	axiosInstance.delete(
		`/projects/${projectId}/task/${taskId}/subtask/${subtaskId}/comments/${commentId}/replies/${replyId}`,
	);

export const editTaskCommentReply = (
	projectId,
	taskId,
	commentId,
	replyId,
	payload,
) =>
	axiosInstance.patch(
		`/projects/${projectId}/task/${taskId}/comments/${commentId}/replies/${replyId}`,
		payload,
	);

export const editSubtaskCommentReply = (
	projectId,
	taskId,
	subtaskId,
	commentId,
	replyId,
	payload,
) =>
	axiosInstance.patch(
		`/projects/${projectId}/task/${taskId}/subtask/${subtaskId}/comments/${commentId}/replies/${replyId}`,
		payload,
	);

// Reactions
export const toggleTaskCommentReaction = (
	projectId,
	taskId,
	commentId,
	emoji,
) =>
	axiosInstance.post(
		`/projects/${projectId}/task/${taskId}/comments/${commentId}/reactions`,
		{ emoji },
	);

export const toggleSubtaskCommentReaction = (
	projectId,
	taskId,
	subtaskId,
	commentId,
	emoji,
) =>
	axiosInstance.post(
		`/projects/${projectId}/task/${taskId}/subtask/${subtaskId}/comments/${commentId}/reactions`,
		{ emoji },
	);

export const toggleTaskCommentReplyReaction = (
	projectId,
	taskId,
	commentId,
	replyId,
	emoji,
) =>
	axiosInstance.post(
		`/projects/${projectId}/task/${taskId}/comments/${commentId}/replies/${replyId}/reactions`,
		{ emoji },
	);

export const toggleSubtaskCommentReplyReaction = (
	projectId,
	taskId,
	subtaskId,
	commentId,
	replyId,
	emoji,
) =>
	axiosInstance.post(
		`/projects/${projectId}/task/${taskId}/subtask/${subtaskId}/comments/${commentId}/replies/${replyId}/reactions`,
		{ emoji },
	);

// Add a team member to a project
export const addTeamMember = (projectId, memberData) =>
	axiosInstance.post(`/projects/${projectId}/team`, memberData);

export const removeTeamMember = (projectId, memberId, reason) =>
	axiosInstance.delete(`/projects/${projectId}/team/${memberId}`, {
		data: { reason },
	});

// Add an invoice to a project
export const addInvoice = (projectId, invoiceData) =>
	axiosInstance.post(`/projects/${projectId}/invoice`, invoiceData);

// Get invoices for a project
export const getProjectInvoices = (projectId) =>
	axiosInstance.get(`/projects/${projectId}/invoices`);

// Get projects for a specific client
export const getClientProjects = (clientId) =>
	axiosInstance.get(`/projects/client/${clientId}/projects`);

// Create a minimalist project
export const createMinimalistProject = (companyId, projectData) =>
	axiosInstance.post(`/projects/${companyId}/project/minimalist`, projectData);

// Milestone API functions
export const addMilestone = (projectId, milestoneData) =>
	axiosInstance.post(`/projects/${projectId}/milestone`, milestoneData);

export const updateMilestone = (projectId, milestoneId, milestoneData) =>
	axiosInstance.put(
		`/projects/${projectId}/milestone/${milestoneId}`,
		milestoneData,
	);

export const markMilestoneComplete = (projectId, milestoneId) =>
	axiosInstance.put(`/projects/${projectId}/milestone/${milestoneId}/complete`);

export const deleteMilestone = (projectId, milestoneId) =>
	axiosInstance.delete(`/projects/${projectId}/milestone/${milestoneId}`);

// Hour tracking API functions
export const getHourEntries = (projectId, teamMemberId) =>
	axiosInstance.get(`/projects/${projectId}/team/${teamMemberId}/hours`);

export const addHourEntry = (projectId, teamMemberId, hourData) =>
	axiosInstance.post(
		`/projects/${projectId}/team/${teamMemberId}/hours`,
		hourData,
	);

export const updateHourEntry = (
	projectId,
	teamMemberId,
	hourEntryId,
	hourData,
) =>
	axiosInstance.put(
		`/projects/${projectId}/team/${teamMemberId}/hours/${hourEntryId}`,
		hourData,
	);

export const deleteHourEntry = (projectId, teamMemberId, hourEntryId) =>
	axiosInstance.delete(
		`/projects/${projectId}/team/${teamMemberId}/hours/${hourEntryId}`,
	);
